// First: no eval anywhere under the site's CSP (src/config/zodJitless.ts).
import './config/zodJitless';
import React from 'react';
import { ViteReactSSG } from 'vite-react-ssg/single-page';
import { hydrateRoot, createRoot as reactCreateRoot } from 'react-dom/client';
import App from './App';
// The Broadsheet kit (packages/ui-kit, spec--pmt-ui-redesign 5(b)): its --bk-* tokens, which pmt's tokens alias,
// then base.css, which styles only bk-* classes and [data-bk] / .bk-page roots -- nothing outside them changes.
// Pinned to the committed kit 0.4.1 copy (0.4.0 the portfolio primitives; 0.3.4 before them; 0.3.1 since spec--ui-kit-essentialist slice U; a later kit change ships
// as a new published version and a pin move here; Storybook's preview keeps the workspace kit).
import '@bim-pdf/ui-kit/published/0.4.1/tokens.css';
import '@bim-pdf/ui-kit/published/0.4.1/base.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/utilities.css';
import './styles/layouts/app.css';
import './styles/layouts/auth.css';
import './styles/layouts/dashboard.css';
import './styles/marketing.css';
import './styles/components/Button.css';
import './styles/components/Form.css';
import './styles/components/Table.css';
import './styles/components/Panels.css';
import './styles/components/Overlays.css';
import './styles/components/DocumentList.css';
import './styles/components/Viewer.css';
import { SsrRouteContext } from './prerender';
import { isPrerenderRoute } from '@/site/routes';
import { shouldRegisterServiceWorker, startServiceWorkerWhenLoaded } from '@/pwa/registerGate';
import { listenForInstallPrompt } from '@/pwa/installPrompt';

// Re-export so vite-react-ssg CLI reads the allowlist from the entry file
// instead of using its default route-discovery pass (which only finds "/").
export { includedRoutes } from './prerender';

// SSG server pass (SSR = true): ViteReactSSG returns the per-route factory.
//
// The fn callback runs before each route's renderToPipeableStream call and
// replaces context.app with a new ReactNode that wraps App in SsrRouteContext.
// Passing the route through React context (not a module-level variable) avoids
// a race condition: vite-react-ssg renders routes concurrently (default
// concurrency=20), so a shared variable would be overwritten mid-render.
// Each renderToPipeableStream call has its own context tree, so this is safe.
//
// Client bundle (SSR = false): this branch is statically replaced and the
// ViteReactSSG call is tree-shaken. The client IIFE inside ViteReactSSG never
// fires anyway (typeof window === "undefined" on the server).
export const createRoot = import.meta.env.SSR
    ? ViteReactSSG(
          <React.StrictMode><App /></React.StrictMode>,
          (ctx) => {
              const route = ctx.routePath ?? '/';
              ctx.app = (
                  <SsrRouteContext.Provider value={route}>
                      <React.StrictMode><App /></React.StrictMode>
                  </SsrRouteContext.Provider>
              );
          },
      )
    : undefined;

if (!import.meta.env.SSR) {
    // Persist the agent opt-in from the URL param across navigations.
    // This must run at the entry point so ?agent=1 works on any route,
    // not just inside the viewer where useViewerTestApi is mounted.
    if (new URL(window.location.href).searchParams.get('agent') === '1') {
        localStorage.setItem('pmt.agent', '1');
    }

    // Offline (docs/offline.md): the service worker registers after load, from a lazy chunk, on the production
    // build on pdfmarkuptool.com / localhost only. The install prompt must be caught from the start (Chromium fires
    // `beforeinstallprompt` once, early).
    listenForInstallPrompt();
    if (shouldRegisterServiceWorker({ prod: import.meta.env.PROD, hostname: window.location.hostname, hasServiceWorker: 'serviceWorker' in navigator })) {
        startServiceWorkerWhenLoaded(() => import('@/pwa/serviceWorker').then((m) => m.startServiceWorker()));
    }

    const container = document.getElementById('root')!;
    const app = <React.StrictMode><App /></React.StrictMode>;

    // Hydrate (not fresh-createRoot) when BOTH conditions hold:
    //   1. The current path is in the prerender allowlist.
    //   2. The container holds server-rendered markup (the SSG build sets
    //      data-server-rendered="true" on the root div).
    // All other paths -- dev mode (no prerender), /local/:slug, /share/:token -- get a fresh
    // CSR render, avoiding the "server did not match" hydration mismatch.
    const isPrerendered = container.dataset.serverRendered === 'true';
    if (isPrerenderRoute(window.location.pathname) && isPrerendered) {
        hydrateRoot(container, app);
    } else {
        reactCreateRoot(container).render(app);
    }
}
